import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  Camera, CameraOff, ScanLine, Wallet, Hourglass, Sparkles, AlertTriangle,
  CheckCircle2, Loader2, ArrowLeft, RotateCw, Coins, User, Phone, Clock,
  KeyboardIcon, KeyRound, ChevronRight, Upload, Flashlight, FlashlightOff
} from 'lucide-react';
import { format } from 'date-fns';

const SCANNER_ID = 'admin-wallet-qr-scanner';

// QR codes are now short server-issued opaque codes (9 chars, base32 Crockford
// alphabet — no 0/O/1/I/L). That keeps the QR sparse + chunky and easy to
// scan. We validate the format client-side before bothering the server.
const QR_CODE_RE = /^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{9}$/;

/**
 * Categorise raw camera errors into operator-friendly copy + retry guidance.
 * html5-qrcode bubbles up DOMException-style errors from getUserMedia.
 */
function classifyCameraError(err) {
  const raw  = (err?.message || String(err) || '').toLowerCase();
  const name = (err?.name || '').toLowerCase();
  if (raw.includes('notallowed') || name.includes('notallowed') || raw.includes('permission')) {
    return {
      kind:  'permission',
      title: 'Camera permission denied',
      hint:  'Click the camera icon in the browser address bar and allow access, then retry.',
    };
  }
  if (raw.includes('notfound')   || name.includes('notfound')   || raw.includes('no camera')) {
    return {
      kind:  'no_device',
      title: 'No camera detected',
      hint:  'This device does not have a usable camera. Paste the QR code below, or switch to Manual OTP.',
    };
  }
  if (raw.includes('notreadable') || name.includes('notreadable') || raw.includes('in use')) {
    return {
      kind:  'in_use',
      title: 'Camera is in use by another app',
      hint:  'Close other apps using the camera (Zoom / Teams / another tab) and try again.',
    };
  }
  if (raw.includes('overconstrained') || name.includes('overconstrained')) {
    return {
      kind:  'overconstrained',
      title: 'Camera constraints unsupported',
      hint:  "Your camera doesn't support back-camera mode. Retry — it will fall back to the default lens.",
    };
  }
  return {
    kind:  'unknown',
    title: 'Could not start camera',
    hint:  raw || 'Please retry, or use the manual code entry below.',
  };
}


/**
 * Admin → Points → Scan QR
 *
 * Flow:
 *   1. Open scanner (asks camera permission)
 *   2. html5-qrcode emits the decoded short code (9 alphanumeric chars)
 *   3. We call /admin/wallet/qr/decode → preview balance + customer name
 *   4. Admin enters amount + clicks Redeem
 *   5. /admin/wallet/redeem-qr atomically deducts the points
 *   6. Show confirmation, allow scanning the next customer
 *
 * Fallback: If the camera is unavailable (no device, permission denied, etc),
 * the admin can paste the short code manually OR switch to Manual OTP.
 */
export default function AdminPointsScanPage() {
  const navigate = useNavigate();

  // Camera/scanner state
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);   // {kind, title, hint}
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  // Manual paste fallback
  const [manualMode, setManualMode] = useState(false);
  const [manualToken, setManualToken] = useState('');

  // Decoded preview state
  const [preview, setPreview] = useState(null);   // { token, customer, balance, qr_seconds_remaining, ... }
  const [decoding, setDecoding] = useState(false);

  // Redemption form state
  const [amount, setAmount] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // result from /redeem-qr

  // ── Cleanup ────────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        // Fire-and-forget — we're unmounting, so we can't await. The
        // library catches its own errors when stopping after unmount.
        Promise.resolve()
          .then(() => s.stop())
          .catch(() => {})
          .finally(() => {
            try { s.clear(); } catch { /* noop */ }
          });
      }
    };
  }, []);

  const stopScanner = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;        // clear ref first so React unmount can't race
    if (s) {
      try { await s.stop(); }   catch { /* ignore stop errors */ }
      try { s.clear(); }        catch { /* ignore clear errors */ }
    }
    setScanning(false);
  };

  const handleDecoded = async (token) => {
    // Prevent re-fires while we're still resolving the previous scan
    if (decoding) return;
    await stopScanner();
    setDecoding(true);
    setCameraError(null);
    try {
      const res  = await adminApi.decodeWalletQR(token);
      const data = res.data;
      if (data.already_redeemed) {
        toast.error('This QR has already been redeemed.');
      } else if (data.qr_seconds_remaining <= 0) {
        toast.error('QR has expired. Ask the customer to refresh.');
      } else {
        toast.success(`Customer: ${data.customer?.full_name || data.customer?.phone}`);
      }
      setPreview({ ...data, token });
    } catch (err) {
      const d   = err?.response?.data?.detail;
      const msg = (typeof d === 'object' && d?.message) ? d.message : (d || 'Invalid QR token');
      toast.error(msg);
    } finally {
      setDecoding(false);
    }
  };

  /**
   * Start the live camera scanner.
   *
   * KISS: we ask for the rear-facing camera via the standard W3C
   * `facingMode: environment` constraint and let the browser pick the
   * appropriate lens. The previous multi-lens picker added a lot of
   * surface area for very little real-world benefit, so it's been
   * removed in favour of this simpler, more reliable path.
   */
  const startScanner = async () => {
    setCameraError(null);
    setPreview(null);
    setConfirmation(null);
    setAmount('');
    setTorchOn(false);
    setTorchSupported(false);

    // Defensive: ensure getUserMedia exists at all (non-HTTPS, embedded webview, etc).
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraError({
        kind:  'unsupported',
        title: 'Browser does not expose camera APIs',
        hint:  'Open this page in Chrome / Safari over HTTPS, or use manual code entry.',
      });
      setManualMode(true);
      return;
    }

    try {
      const qr = new Html5Qrcode(SCANNER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      scannerRef.current = qr;

      await qr.start(
        { facingMode: { ideal: 'environment' } },
        {
          fps: 15,
          qrbox: (vw, vh) => {
            const minDim = Math.min(vw, vh);
            const size   = Math.max(220, Math.floor(minDim * 0.78));
            return { width: size, height: size };
          },
          aspectRatio: 1.0,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
          // Push for a sharper video frame on phones — main back cameras can
          // easily provide 1280x720 and the extra pixels help even though the
          // QR is now chunky (9 chars → 21x21 modules).
          videoConstraints: {
            facingMode: { ideal: 'environment' },
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        (decodedText) => { handleDecoded(decodedText); },
        () => {/* per-frame failures are noisy — ignore */}
      );
      setScanning(true);

      // Detect torch support so we can show a flashlight toggle. Only some
      // browsers / cameras expose `torch` as a settable constraint.
      try {
        const track = qr.getRunningTrackCameraCapabilities?.();
        const caps  = track?.torchFeature?.()?.isSupported?.();
        setTorchSupported(Boolean(caps));
      } catch { /* not supported on this device */ }
    } catch (err) {
      console.error('[Scanner] start error', err);
      const info = classifyCameraError(err);
      setCameraError(info);
      setScanning(false);
      // For "no camera" / "unsupported", auto-open the manual fallback so the
      // admin isn't blocked.
      if (info.kind === 'no_device' || info.kind === 'unsupported') {
        setManualMode(true);
      }
      // Make sure we don't leak the scanner instance
      if (scannerRef.current) {
        try { scannerRef.current.clear(); } catch { /* noop */ }
        scannerRef.current = null;
      }
    }
  };

  const toggleTorch = async () => {
    const s = scannerRef.current;
    if (!s || !torchSupported) return;
    try {
      const torchFeature = s.getRunningTrackCameraCapabilities?.()?.torchFeature?.();
      const next = !torchOn;
      await torchFeature?.apply?.(next);
      setTorchOn(next);
    } catch (err) {
      console.warn('[Scanner] torch toggle failed', err);
      toast.error('Could not toggle the camera flash');
    }
  };

  /**
   * Fallback: scan a QR code from a screenshot / photo the admin uploads.
   * Useful when the live camera detection is flaky (low light, glare,
   * tiny QR in customer's screen, etc).
   */
  const handleImageFile = async (file) => {
    if (!file) return;
    // The host div can only host one Html5Qrcode instance at a time. Stop
    // the live scanner first if it's running.
    await stopScanner();
    setDecoding(true);
    setCameraError(null);
    let qr = null;
    try {
      qr = new Html5Qrcode(SCANNER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      const decoded = await qr.scanFile(file, /* showImage */ false);
      // handleDecoded clears scannerRef + state, so we don't double-stop
      await handleDecoded(decoded);
    } catch (err) {
      console.warn('[Scanner] image decode failed', err);
      toast.error('No QR detected in that image. Try a sharper / closer crop.');
    } finally {
      try { qr?.clear?.(); } catch { /* noop */ }
      setDecoding(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleManualDecode = async () => {
    // The on-screen code is 9 uppercase alphanumeric chars. Normalise +
    // strip whitespace so the admin can type with spaces or lower case.
    const tok = (manualToken || '').replace(/\s+/g, '').toUpperCase();
    if (!QR_CODE_RE.test(tok)) {
      toast.error('Code must be 9 letters/numbers, e.g. AB23CDEF9');
      return;
    }
    setManualToken('');
    await handleDecoded(tok);
  };

  const handleRedeem = async () => {
    const amt = parseInt(amount, 10);
    if (!preview?.token) return;
    if (!amt || amt <= 0) {
      toast.error('Enter an amount greater than 0');
      return;
    }
    if (amt > (preview.balance?.available || 0)) {
      toast.error(`Customer only has ${preview.balance?.available || 0} points available`);
      return;
    }
    setRedeeming(true);
    try {
      const res = await adminApi.redeemWalletQR(preview.token, amt);
      setConfirmation(res.data);
      toast.success(`Redeemed ${amt} pts from ${res.data.customer?.full_name || 'customer'}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (typeof d === 'object' && d?.message) ? d.message : (d || 'Redemption failed');
      toast.error(msg);
    } finally {
      setRedeeming(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setConfirmation(null);
    setAmount('');
    setCameraError(null);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <AdminShell title="Points · Scan QR">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="admin-points-scan-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#F2F2F2] flex items-center gap-2">
              <Wallet size={20} className="text-[#D4A843]" /> QR Redemption
            </h1>
            <p className="text-sm text-[#B9B9B9] mt-1">
              Scan a customer's wallet QR code to deduct Scratch Points.
            </p>
          </div>
        </div>

        {/* CONFIRMATION SCREEN ────────────────────────────────────────────── */}
        {confirmation && (
          <Card
            data-testid="redeem-confirmation"
            className="p-6 rounded-2xl border"
            style={{ background: 'rgba(74,222,128,0.06)', borderColor: 'rgba(74,222,128,0.35)' }}
          >
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(74,222,128,0.18)' }}>
                <CheckCircle2 size={22} className="text-[#4ADE80]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-[#F2F2F2]">Redemption Successful</h2>
                <p className="text-[#B9B9B9] text-sm mt-1">
                  <strong className="text-[#4ADE80] font-mono">{confirmation.redeemed} pts</strong>
                  {' '}deducted from <strong className="text-[#F2F2F2]">{confirmation.customer?.full_name}</strong>
                </p>
                <p className="text-xs text-[#6B6B6B] mt-2">
                  New balance: <span className="text-[#D4A843] font-mono font-semibold">{confirmation.balance?.available}</span> pts
                  {' · '}Txn ID: <code className="text-[#6B6B6B]">{confirmation.transaction?.id?.slice(0, 8)}…</code>
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button
                onClick={() => { reset(); startScanner(); }}
                className="flex-1 btn-gold rounded-xl"
                data-testid="redeem-scan-next-btn"
              >
                <ScanLine size={14} className="mr-2" /> Scan Next Customer
              </Button>
              <Button
                variant="outline"
                onClick={reset}
                className="border-[rgba(255,255,255,0.1)] text-[#B9B9B9]"
              >
                <ArrowLeft size={14} className="mr-1" /> Done
              </Button>
            </div>
          </Card>
        )}

        {/* PREVIEW + REDEEM FORM (after a successful scan) ──────────────── */}
        {!confirmation && preview && (
          <Card data-testid="redeem-preview-card" className="p-6 rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] space-y-5">
            {/* Customer */}
            <div className="flex items-start justify-between gap-3 pb-4 border-b border-[rgba(255,255,255,0.06)]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(212,168,67,0.12)]">
                  <User size={18} className="text-[#D4A843]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#F2F2F2] truncate">{preview.customer?.full_name || '—'}</p>
                  <p className="text-xs text-[#B9B9B9] flex items-center gap-1 mt-0.5">
                    <Phone size={10} /> {preview.customer?.phone || preview.customer?.phone_e164}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { reset(); startScanner(); }}
                className="text-xs text-[#6B6B6B] hover:text-[#B9B9B9] flex items-center gap-1"
                data-testid="redeem-rescan-btn"
              >
                <RotateCw size={11} /> Rescan
              </button>
            </div>

            {/* Balance breakdown */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-xl bg-[rgba(212,168,67,0.06)] border border-[rgba(212,168,67,0.2)]">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Coins size={11} className="text-[#D4A843]" />
                  <p className="text-[10px] text-[#B9B9B9] uppercase tracking-widest">Available</p>
                </div>
                <p data-testid="preview-available" className="text-2xl font-bold font-mono text-[#D4A843]">{preview.balance?.available ?? 0}</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-[rgba(255,77,77,0.04)] border border-[rgba(255,77,77,0.18)]">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Hourglass size={11} className="text-[#FF8A8A]" />
                  <p className="text-[10px] text-[#B9B9B9] uppercase tracking-widest">Expiring</p>
                </div>
                <p data-testid="preview-expiring" className="text-2xl font-bold font-mono text-[#FF8A8A]">{preview.balance?.expiring ?? 0}</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-[rgba(96,165,250,0.04)] border border-[rgba(96,165,250,0.18)]">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Sparkles size={11} className="text-[#60A5FA]" />
                  <p className="text-[10px] text-[#B9B9B9] uppercase tracking-widest">Loyalty</p>
                </div>
                <p className="text-2xl font-bold font-mono text-[#60A5FA]">{preview.balance?.non_expiring ?? 0}</p>
              </div>
            </div>

            {preview.balance?.next_expiry_at && (
              <p className="text-[11px] text-[#6B6B6B] text-center flex items-center justify-center gap-1">
                <Clock size={10} />
                Next expiry: {format(new Date(preview.balance.next_expiry_at), 'MMM d, h:mm a')}
              </p>
            )}

            {preview.already_redeemed ? (
              <div className="p-4 rounded-xl bg-[rgba(255,77,77,0.06)] border border-[rgba(255,77,77,0.3)] flex items-start gap-3">
                <AlertTriangle size={16} className="text-[#FF4D4D] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#FF8A8A]">This QR has already been redeemed. Please ask the customer to refresh their QR.</p>
              </div>
            ) : (
              <>
                {/* Amount input */}
                <div className="space-y-2">
                  <Label className="text-[#B9B9B9] text-xs">Points to Redeem</Label>
                  <Input
                    type="number"
                    min={1}
                    max={preview.balance?.available || 0}
                    data-testid="redeem-amount-input"
                    placeholder={`Max ${preview.balance?.available ?? 0}`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] text-2xl font-mono h-14 text-center"
                    autoFocus
                  />
                  <div className="flex gap-2 justify-center text-[11px]">
                    {[100, 250, 500, preview.balance?.available || 0].filter((v, i, arr) => v > 0 && arr.indexOf(v) === i).map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setAmount(String(preset))}
                        className="px-3 py-1 rounded-lg bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] text-[#B9B9B9]"
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  data-testid="redeem-confirm-btn"
                  className="w-full h-12 btn-gold rounded-xl font-semibold text-base"
                  onClick={handleRedeem}
                  disabled={redeeming || !amount || parseInt(amount, 10) <= 0}
                >
                  {redeeming ? <><Loader2 size={16} className="animate-spin mr-2" /> Processing…</> : `Redeem ${amount || 0} pts`}
                </Button>
              </>
            )}
          </Card>
        )}

        {/* SCANNER / IDLE STATE ────────────────────────────────────────────── */}
        {!preview && !confirmation && (
          <Card className="p-6 rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] space-y-4">
            {/*
              IMPORTANT: The element with id={SCANNER_ID} is owned by the
              `html5-qrcode` library — it injects <video>/<canvas> children
              directly into the DOM. We therefore render ZERO JSX children
              inside it, otherwise React's reconciler hits
              `removeChild: node is not a child` and the whole route crashes.
              All placeholder UI is rendered as an absolute-positioned
              SIBLING overlay.
            */}
            <div className="relative w-full aspect-square max-w-[420px] mx-auto rounded-2xl overflow-hidden bg-black">
              <div
                id={SCANNER_ID}
                data-testid="qr-scanner-mount"
                className="absolute inset-0"
              />
              {!scanning && !decoding && (
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  aria-hidden="true"
                >
                  <div className="text-center px-6">
                    <ScanLine size={42} className="mx-auto mb-3 text-[#D4A843]" />
                    <p className="text-[#F2F2F2] font-semibold mb-1">Camera off</p>
                    <p className="text-xs text-[#B9B9B9]">Click below to start scanning</p>
                  </div>
                </div>
              )}
              {decoding && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm pointer-events-none"
                  aria-hidden="true"
                >
                  <div className="text-center">
                    <Loader2 size={32} className="animate-spin text-[#D4A843] mx-auto mb-2" />
                    <p className="text-xs text-[#B9B9B9]">Validating QR…</p>
                  </div>
                </div>
              )}
            </div>

            {/* ── Camera error block (replaces the old red strip) ── */}
            {cameraError && (
              <div
                data-testid="scanner-camera-error"
                className="p-4 rounded-xl bg-[rgba(255,77,77,0.06)] border border-[rgba(255,77,77,0.3)] space-y-3"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle size={15} className="text-[#FF4D4D] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#FF8A8A]">{cameraError.title}</p>
                    <p className="text-[12px] text-[#B9B9B9] mt-1">{cameraError.hint}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setCameraError(null); startScanner(); }}
                    className="border-[rgba(255,255,255,0.12)] text-[#B9B9B9]"
                    data-testid="scanner-retry-btn"
                  >
                    <RotateCw size={12} className="mr-1.5" /> Retry camera
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setCameraError(null); setManualMode(true); }}
                    className="border-[rgba(212,168,67,0.4)] text-[#D4A843] hover:bg-[rgba(212,168,67,0.08)]"
                    data-testid="scanner-fallback-manual-btn"
                  >
                    <KeyboardIcon size={12} className="mr-1.5" /> Type code instead
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/admin/points/manual-redeem')}
                    className="border-[rgba(255,255,255,0.12)] text-[#B9B9B9]"
                    data-testid="scanner-switch-otp-btn"
                  >
                    <KeyRound size={12} className="mr-1.5" /> Use Manual OTP <ChevronRight size={12} className="ml-0.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Live scanning guidance — only when camera is on */}
            {scanning && !cameraError && (
              <p className="text-[11px] text-[#B9B9B9] text-center px-2">
                Hold the QR <strong className="text-[#D4A843]">15-20 cm away</strong> inside
                the box · wait a moment for autofocus
              </p>
            )}

            {/* Torch toggle — only visible when supported & scanner running */}
            {scanning && torchSupported && (
              <div className="flex justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleTorch}
                  className="border-[rgba(255,255,255,0.12)] text-[#B9B9B9]"
                  data-testid="scanner-torch-btn"
                >
                  {torchOn
                    ? <><FlashlightOff size={13} className="mr-1.5" /> Torch off</>
                    : <><Flashlight size={13} className="mr-1.5" /> Torch on</>}
                </Button>
              </div>
            )}

            <div className="flex gap-2">
              {!scanning ? (
                <Button
                  data-testid="start-scanner-btn"
                  className="flex-1 btn-gold rounded-xl h-11"
                  onClick={startScanner}
                  disabled={decoding}
                >
                  <Camera size={15} className="mr-2" /> Start Scanner
                </Button>
              ) : (
                <Button
                  data-testid="stop-scanner-btn"
                  variant="outline"
                  className="flex-1 border-[rgba(255,255,255,0.1)] rounded-xl h-11"
                  onClick={stopScanner}
                >
                  <CameraOff size={15} className="mr-2" /> Stop Scanner
                </Button>
              )}
              {/* Upload image fallback — useful when the live camera struggles */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="scanner-file-input"
                onChange={(e) => handleImageFile(e.target.files?.[0])}
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-[rgba(255,255,255,0.1)] text-[#B9B9B9] rounded-xl h-11"
                data-testid="scanner-upload-btn"
                disabled={decoding}
                title="Scan from a saved screenshot"
              >
                <Upload size={14} className="mr-1.5" /> Upload
              </Button>
              <Button
                variant="outline"
                onClick={() => setManualMode((v) => !v)}
                className="border-[rgba(255,255,255,0.1)] text-[#B9B9B9] rounded-xl h-11"
                data-testid="scanner-toggle-manual-btn"
                disabled={scanning || decoding}
              >
                <KeyboardIcon size={14} className="mr-1.5" />
                {manualMode ? 'Hide' : 'Type'}
              </Button>
            </div>

            {/* ── Manual code entry fallback ── */}
            {manualMode && (
              <div
                data-testid="manual-token-section"
                className="p-4 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)] space-y-3"
              >
                <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                  <KeyboardIcon size={11} /> Type the 9-character code under the QR
                </Label>
                <Input
                  data-testid="manual-token-input"
                  maxLength={11}      // 9 chars + room for spacing
                  placeholder="e.g. AB23CDEF9"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value.toUpperCase())}
                  className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] text-xl font-mono tracking-widest h-12 text-center uppercase"
                  spellCheck={false}
                  autoCapitalize="characters"
                  autoCorrect="off"
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleManualDecode}
                    disabled={decoding || !manualToken.trim()}
                    className="btn-gold rounded-xl"
                    data-testid="manual-token-decode-btn"
                  >
                    {decoding
                      ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Decoding…</>
                      : <>Decode & Preview</>}
                  </Button>
                </div>
                <p className="text-[11px] text-[#6B6B6B]">
                  The code is shown right under the customer's QR. It expires 60 seconds after issue.
                </p>
              </div>
            )}

            <p className="text-[11px] text-[#6B6B6B] text-center">
              QR codes expire 60 seconds after issue · scanning works in well-lit conditions
            </p>
          </Card>
        )}
      </div>
    </AdminShell>
  );
}
